import { k as _asyncToGenerator, l as _regeneratorRuntime, f as _objectSpread2$1 } from './_rollupPluginBabelHelpers-047e4aae.js';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { d as _taggedTemplateLiteral, a as _objectSpread2, _ as _slicedToArray, c as _extends } from './_rollupPluginBabelHelpers-b9ad31fa-7ae21c08.js';
import styled from 'styled-components';
import variant from '@styled-system/variant';

var DEFAULT_TRANSITION = {
  transition: "all 0.4s ease"
};
var TIMEOUTS = {
  TRANSITION: 50,
  UNMOUNT: 80
};

var _templateObject;
var defaultStyles = function defaultStyles() {
  return _objectSpread2({}, DEFAULT_TRANSITION);
};
var EntryVariants = variant({
  prop: "entry",
  scale: "entryStyles",
  variants: {}
});
var ExitVariants = variant({
  prop: "exit",
  scale: "exitStyles",
  variants: {}
});
var ActiveVariant = variant({
  prop: "active",
  scale: "activeStyle"
});
var AnimateWrapper = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n  ", "\n"])), defaultStyles, function (props) {
  var active = props.active,
    triggerExit = props.triggerExit;
  if (active) return ActiveVariant(props);
  if (triggerExit) return ExitVariants(props);
  return EntryVariants(props);
});
var Animate = function Animate(props) {
  var active = props.active;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    wrapperActive = _useState2[0],
    setWrapperActive = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    wrapperUnmount = _useState4[0],
    setWrapperUnmount = _useState4[1];
  useEffect(function () {
    if (props.active) setWrapperUnmount(false);
    setTimeout(function () {
      setWrapperActive(props.active);
    }, TIMEOUTS.TRANSITION);
  }, [props]);
  var transitionEnd = function transitionEnd() {
    if (!wrapperActive) {
      setTimeout(function () {
        setWrapperUnmount(true);
      }, TIMEOUTS.UNMOUNT);
    }
  };
  return !wrapperUnmount ? /*#__PURE__*/React.createElement(AnimateWrapper
  // eslint-disable-next-line react/jsx-props-no-spreading
  , _extends({}, props, {
    active: wrapperActive,
    onTransitionEnd: transitionEnd,
    triggerExit: !active
  })) : null;
};
Animate.defaultProps = {
  active: true,
  entry: "fade",
  exit: "fade",
  style: {}
};
Animate.propTypes = {
  /** Determines if element is visible */
  active: PropTypes.bool,
  /** Name of entry animation */
  entry: PropTypes.string,
  /** Name of exit animation */
  exit: PropTypes.string,
  /** Pass custom styles (if applicable) */
  style: PropTypes.shape({})
};
Animate.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Animate",
  "props": {
    "active": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines if element is visible"
    },
    "entry": {
      "defaultValue": {
        "value": "\"fade\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Name of entry animation"
    },
    "exit": {
      "defaultValue": {
        "value": "\"fade\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Name of exit animation"
    },
    "style": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "type": {
        "name": "shape",
        "value": {}
      },
      "required": false,
      "description": "Pass custom styles (if applicable)"
    }
  }
};

/**
 * checks whether backend has sent an error
 * @param status status code of api call
 */
var hasError = function hasError(status) {
  var ERROR_HEADERS = [400, 403, 405, 500];
  return ERROR_HEADERS.includes(status);
};
function getCookie(name) {
  var cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    var cookies = document.cookie.split(";");
    for (var i = 0; i < cookies.length; i += 1) {
      var cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === "".concat(name, "=")) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}
function csrfSafeMethod(method) {
  // these HTTP methods do not require CSRF protection
  return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
}
function sameOrigin(url) {
  // test that a given url is a same-origin URL
  // url could be relative or scheme relative or absolute
  var host = document.location.host; // host + port
  var protocol = document.location.protocol;
  var srOrigin = "//".concat(host);
  var origin = protocol + srOrigin;
  // Allow absolute or scheme relative URLs to same origin
  return url === origin || url.slice(0, origin.length + 1) === "".concat(origin, "/") || url === srOrigin || url.slice(0, srOrigin.length + 1) === "".concat(srOrigin, "/") ||
  // or any other URL that isn't scheme relative or absolute i.e relative.
  !/^(\/\/|http:|https:).*/.test(url);
}
var APIRequest = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(url, method) {
    var data,
      headers,
      response,
      getFormattedData,
      request,
      contentType,
      json,
      _args = arguments;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          data = _args.length > 2 && _args[2] !== undefined ? _args[2] : null;
          headers = {
            "Content-Type": "application/json"
          };
          // returns formatted data according to its type
          getFormattedData = function getFormattedData() {
            return data instanceof FormData ? data : JSON.stringify(data);
          };
          if (!csrfSafeMethod(method) && sameOrigin(url)) {
            headers["X-CSRFToken"] = getCookie("csrftoken");
          }
          request = new Request(url, {
            method: method,
            headers: new Headers(headers),
            body: method !== "GET" && data ? getFormattedData() : undefined
          });
          _context.prev = 5;
          _context.next = 8;
          return fetch(request);
        case 8:
          response = _context.sent;
          _context.next = 14;
          break;
        case 11:
          _context.prev = 11;
          _context.t0 = _context["catch"](5);
          return _context.abrupt("return", Promise.reject(_context.t0));
        case 14:
          _context.prev = 14;
          contentType = response.headers.get("content-type");
          if (!(contentType && contentType.indexOf("application/json") !== -1)) {
            _context.next = 25;
            break;
          }
          _context.next = 19;
          return response.json();
        case 19:
          json = _context.sent;
          if (json) {
            _context.next = 22;
            break;
          }
          return _context.abrupt("return", Promise.reject(new Error("Invalid JSON ".concat(json))));
        case 22:
          if (!hasError(response.status)) {
            _context.next = 24;
            break;
          }
          return _context.abrupt("return", Promise.resolve({
            success: false,
            response: json
          }));
        case 24:
          return _context.abrupt("return", Promise.resolve({
            success: true,
            response: json
          }));
        case 25:
          return _context.abrupt("return", Promise.reject(new Error("API Request Failed")));
        case 28:
          _context.prev = 28;
          _context.t1 = _context["catch"](14);
          return _context.abrupt("return", Promise.reject(_context.t1));
        case 31:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[5, 11], [14, 28]]);
  }));
  return function APIRequest(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
function triggerMixPanelEvent(eventTitle, payload) {
  var _window$mixpanel, _botData$business;
  var botData = JSON.parse(localStorage.getItem("ww_iva"));
  (_window$mixpanel = window.mixpanel) === null || _window$mixpanel === void 0 || _window$mixpanel.track(eventTitle, _objectSpread2$1({
    "Bot Name": botData === null || botData === void 0 ? void 0 : botData.title,
    "Bot ID": botData === null || botData === void 0 ? void 0 : botData.id,
    Business: botData.isSmartSkill ? botData.business_via_name : botData === null || botData === void 0 || (_botData$business = botData.business) === null || _botData$business === void 0 || (_botData$business = _botData$business[0]) === null || _botData$business === void 0 ? void 0 : _botData$business.via_name,
    Account: botData === null || botData === void 0 ? void 0 : botData.client
  }, payload));
}

export { Animate as A, APIRequest as a, triggerMixPanelEvent as t };
